/**
 * Banner Content Configuration
 *
 * Defines the informational banners shown at the top of various pages.
 * Each banner provides context about the page and links to related functionality.
 */

export interface BannerConfig {
  /** Unique page identifier */
  pageId: string;
  /** Banner title (shown prominently) */
  title: string;
  /** Explanatory description of the page's purpose */
  description: string;
  /** Optional: ID of the tour to start when user clicks "Start rondleiding" */
  tourId?: string;
  /** Optional: Links to related pages */
  links?: { label: string; href: string }[];
}

export const BANNER_CONFIGS: Record<string, BannerConfig> = {
  'dd-settings': {
    pageId: 'dd-settings',
    title: 'Due Diligence Configuratie',
    description:
      'Beheer hier de categorieën en vragen voor due diligence analyses. Configureer welke documenten nodig zijn en hoe de AI ze moet verwerken.',
    tourId: 'ddSettingsTour',
    links: [
      { label: 'DD overzicht', href: '/due-diligence' },
      { label: 'Bestandslabels', href: '/settings' },
    ],
  },
  'dd-category-editor': {
    pageId: 'dd-category-editor',
    title: 'Categorie Bewerken',
    description:
      'Pas vragen en antwoordopties aan voor deze due diligence categorie. Koppel bestandslabels aan vragen voor automatische documentanalyse.',
    tourId: 'ddConditionsTour',
    links: [{ label: 'Bestandslabels', href: '/settings' }],
  },
  dropzone: {
    pageId: 'dropzone',
    title: 'Document Dropzone',
    description:
      'Sleep documenten hierheen of selecteer bestanden om te uploaden. De AI classificeert automatisch het documenttype en extraheert relevante informatie.',
    tourId: 'dropzoneTour',
    links: [
      { label: 'Labels configureren', href: '/settings' },
      { label: 'DD vragen instellen', href: '/settings' },
    ],
  },
  'file-labels': {
    pageId: 'file-labels',
    title: 'Bestandslabels',
    description:
      'Configureer bestandslabels voor documentclassificatie. Stel AI-analyse in per label en definieer extractieregels voor automatische gegevensverwerking.',
    tourId: 'fileLabelsTour',
    links: [
      { label: 'Dropzone', href: '/buildings' },
      { label: 'DD categorieën', href: '/settings' },
    ],
  },
  'dd-operational': {
    pageId: 'dd-operational',
    title: 'Due Diligence Overzicht',
    description:
      'Bekijk de status van alle due diligence vragen voor dit gebouw. Groen = beantwoord, oranje = aandacht nodig, rood = ontbrekend.',
    tourId: 'ddOperationalTour',
    links: [{ label: 'DD vragen configureren', href: '/settings' }],
  },
  'asset-categories': {
    pageId: 'asset-categories',
    title: 'Voorzieningen Categorieën',
    description:
      'Beheer categorieën en types voor voorzieningen (CV-ketels, liften, branddetectie, etc.). Definieer aangepaste velden per type.',
    tourId: 'voorzieningenTour',
    links: [{ label: 'Dropzone', href: '/buildings' }],
  },
  'dd-workflow-intro': {
    pageId: 'dd-workflow-intro',
    title: 'Complete DD Workflow',
    description:
      'Leer hoe het complete due diligence proces werkt: van configuratie tot documentanalyse en rapportage.',
    tourId: 'fullDDWorkflowTour',
    links: [
      { label: 'DD configuratie', href: '/settings' },
      { label: 'Dropzone', href: '/buildings' },
      { label: 'DD overzicht', href: '/due-diligence' },
    ],
  },
  leegstand: {
    pageId: 'leegstand',
    title: 'Leegstand beheren',
    description:
      "Overzicht van alle units die momenteel of binnenkort leeg komen. Publiceer ze via Whise op Immoweb, koppel de Immoweb-referentie, hou de afsprakenkalender online en volg de kandidaten op. Start de rondleiding hieronder voor een korte uitleg.",
    tourId: 'leegstandTour',
    links: [
      { label: 'Kandidaten', href: '/rental/candidacies' },
      { label: 'Whise (nieuw tabblad)', href: 'https://web.whise.eu/login.html' },
    ],
  },
};
